import type { Message } from './state';

/**
 * State payload sent/received for synchronization between widget instances.
 */
export interface WidgetSyncState {
  messages: SerializedMessage[];
  inputText: string;
  sessionId: string;
  isAwaitingResponse: boolean;
  /** Current follow-up questions from last bot response */
  followUpQuestions: string[] | null;
}

/**
 * Serialized message for postMessage transport.
 * Date objects are converted to ISO strings.
 */
export interface SerializedMessage {
  id: string;
  role: 'user' | 'bot';
  content: string;
  timestamp: string; // ISO string
  status: 'sending' | 'sent' | 'error';
  messageId?: string;
  rating?: 'positive' | 'negative';
  canProvideEmail?: boolean;
  unansweredQuestionId?: string;
  sources?: Array<{ pageUrl: string; title?: string }>;
  emailSubmitted?: boolean;
  followUpQuestions?: string[];
}

/**
 * Widget -> Page: state-update message.
 * Sent when conversation state changes.
 */
export interface StateSyncUpdateMessage {
  type: 'onboardi:state-update';
  payload: WidgetSyncState & {
    instanceId: string;
  };
}

/**
 * Page -> Widget: state-sync message.
 * Relays state from one instance to another.
 */
export interface StateSyncReceiveMessage {
  type: 'onboardi:state-sync';
  payload: WidgetSyncState;
}

/**
 * Page -> Widget: visibility control message.
 */
export interface VisibilityControlMessage {
  type: 'onboardi:visibility';
  payload: {
    action: 'show' | 'hide' | 'open' | 'close';
  };
}

/**
 * Union type for all sync-related postMessage types.
 */
export type SyncMessage =
  | StateSyncUpdateMessage
  | StateSyncReceiveMessage
  | VisibilityControlMessage;

/**
 * Callback type for state sync reception.
 */
export type OnStateSyncCallback = (state: WidgetSyncState) => void;

/**
 * Callback type for visibility change.
 */
export type OnVisibilityChangeCallback = (action: 'show' | 'hide' | 'open' | 'close') => void;

/**
 * Configuration for StateSyncService.
 */
export interface StateSyncConfig {
  instanceId: string;
  sessionId: string;
  parentOrigin: string;
  onStateSync: OnStateSyncCallback;
  onVisibilityChange: OnVisibilityChangeCallback;
}

/**
 * Serialize a Message for postMessage transport.
 */
export function serializeMessage(msg: Message): SerializedMessage {
  return {
    ...msg,
    timestamp: msg.timestamp.toISOString(),
  };
}

/**
 * Deserialize a SerializedMessage back to Message.
 */
export function deserializeMessage(msg: SerializedMessage): Message {
  return {
    ...msg,
    timestamp: new Date(msg.timestamp),
  };
}

/**
 * Serialize an array of messages.
 */
export function serializeMessages(messages: Message[]): SerializedMessage[] {
  return messages.map(serializeMessage);
}

/**
 * Deserialize an array of messages.
 */
export function deserializeMessages(messages: SerializedMessage[]): Message[] {
  return messages.map(deserializeMessage);
}
